import type { IAppPreferences } from '@erp/native/interfaces';

/**
 * Boot hint — a tiny persisted snapshot of "where the user was last".
 *
 * Why: a cold launch normally has to await auth.getSession() then
 * tenants.getCurrent() before it can decide between /, /welcome, /login.
 * Each is a network round-trip; together they make the splash linger
 * 500-1500ms — long enough to feel sluggish.
 *
 * The hint is read SYNCHRONOUSLY at boot (we mirror it into localStorage
 * via the bridge so it's available before async storage settles), letting
 * us render the most likely landing route immediately. Validation still
 * runs in the background; if the hint was stale we correct the route.
 *
 * The hint is non-authoritative (only used for routing optimism). The
 * source of truth remains the Supabase session + tenants table.
 */
export type BootHint =
  | { kind: 'unknown' }
  | { kind: 'logged_out' }
  | { kind: 'no_tenant'; userId: string }
  | { kind: 'ready'; userId: string; tenantId: string };

const KEY = 'erp.boot.hint.v1';
// Mirror to localStorage so the *very first* synchronous render can pick
// it up. The Tauri Store load() is async and would otherwise flash an
// empty splash before our hint arrives.
const SYNC_KEY = 'erp.boot.hint.v1.sync';

export function readBootHintSync(): BootHint {
  try {
    const raw = window.localStorage.getItem(SYNC_KEY);
    if (!raw) return { kind: 'unknown' };
    const parsed = JSON.parse(raw) as BootHint;
    // Defensive: validate the shape so a corrupt entry doesn't crash boot.
    if (!parsed || typeof parsed !== 'object' || !('kind' in parsed)) return { kind: 'unknown' };
    return parsed;
  } catch {
    return { kind: 'unknown' };
  }
}

/**
 * Update the hint after every auth / tenant transition. Writes to both
 * localStorage (sync, for next boot's instant read) and the bridge's
 * preferences (long-lived, primary store on desktop).
 */
export async function writeBootHint(prefs: IAppPreferences, hint: BootHint): Promise<void> {
  const json = JSON.stringify(hint);
  try {
    window.localStorage.setItem(SYNC_KEY, json);
  } catch {
    // localStorage full / disabled — ignore, the persistent copy still works.
  }
  try {
    await prefs.set(KEY, hint);
  } catch {
    // Store unavailable — the sync mirror still serves us next boot.
  }
}

export function clearBootHint(prefs: IAppPreferences): Promise<void> {
  try {
    window.localStorage.removeItem(SYNC_KEY);
  } catch {
    /* ignore */
  }
  return prefs.remove(KEY).catch(() => {});
}
